// Geometry of object profiles (CIN-15126, phase 3): round and elliptic stages with curved steps, curved barriers,
// railings and walls. Turns an `ObjectProfile` (object-local plan mm, y toward the screen at rotation 0) into
// rings and polylines in the object's 3D-local space (meters, x across, z toward the audience). Pure; relative
// imports only.
//
// DECISIONS
// - A profile point (px, py) in plan mm becomes the 3D-local point (px / 1000, -py / 1000): the plan's y toward
//   the screen is the model's -z, exactly like `objectWorldTransform` does for placement. A stage's front (its
//   lip, toward the audience) is therefore the plan -y side.
// - Steps are BANDS between offset rings: ring k is the outline moved outward by k x tread, band k is solid up
//   to `stageHeight x (count + 1 - k) / (count + 1)`. A closed 'all' step is the whole ring k (it overlaps the
//   taller inside, which hides it); a partial step is the band between ring k - 1 and ring k over the chosen points.
// - `steps.edges` picks the outline: 'all', 'front' (every tessellated point whose outward normal points at least
//   `FRONT_NORMAL` toward the audience) or a list of profile path segment indices.
// - Offsets use a very large miter limit so the rings keep the base ring's point count and stay index-aligned.
// - Openings are arc-length ranges of the path in mm (from the first point); on a closed path the path is cut at
//   its first point.

import { tessellateEdge } from '../outline/arc.ts';
import { offsetPath } from '../outline/offset.ts';
import type { ObjectProfile, PlanPoint } from '../outline/types.ts';

/** A point of an object's 3D-local floor plan, meters. */
export type LocalPoint = { x: number; z: number };

/** Steps on the 'front' edges: the outward normal must point at least this much toward the audience (plan -y). */
export const FRONT_NORMAL = 0.25;
const TOLERANCE_MM = 15;
const NO_BEVEL = 1e6;

export const planToLocal = (p: PlanPoint): LocalPoint => ({ x: p.x / 1000 + 0, z: -p.y / 1000 + 0 });

export function ringArea(points: readonly PlanPoint[]): number {
    let twice = 0;
    points.forEach((p, i) => {
        const q = points[(i + 1) % points.length];
        twice += p.x * q.y - q.x * p.y;
    });
    return twice / 2;
}

/**
 * The profile path as a polyline in plan mm. `segmentOf[i]` is the index of the profile path segment the piece from
 * point `i` to point `i + 1` belongs to. A closed path has no repeated last point.
 */
export function profilePlanPoints(
    profile: ObjectProfile,
    toleranceMm = TOLERANCE_MM
): { points: PlanPoint[]; segmentOf: number[] } {
    const { points: raw, closed } = profile.path;
    const points: PlanPoint[] = [];
    const segmentOf: number[] = [];
    const count = closed ? raw.length : raw.length - 1;
    for (let i = 0; i < count; i += 1) {
        const a = raw[i];
        const b = raw[(i + 1) % raw.length];
        const run = tessellateEdge(a, b, a.bulge ?? 0, toleranceMm);
        for (let k = 0; k < run.length - 1; k += 1) {
            points.push(run[k]);
            segmentOf.push(i);
        }
    }
    if (!closed && raw.length > 0) {
        const last = raw[raw.length - 1];
        points.push({ x: last.x, y: last.y });
        segmentOf.push(Math.max(0, raw.length - 2));
    }
    return { points, segmentOf };
}

/** The ring in 3D-local meters. */
export const toLocalRing = (points: readonly PlanPoint[]): LocalPoint[] => points.map(planToLocal);

/** The closed profile path moved `distanceMm` outward (inward when negative), in 3D-local meters. */
export function offsetRingLocal(profile: ObjectProfile, distanceMm: number): LocalPoint[] {
    const { points } = profilePlanPoints(profile);
    // Outward is the right of travel for a counter-clockwise ring.
    const ccw = ringArea(points) < 0 ? [...points].reverse() : points;
    return toLocalRing(offsetPath(ccw, true, distanceMm, NO_BEVEL));
}

export type StepBand = {
    /** The band's outline, 3D-local meters (any orientation; extrusion handles both). */
    ring: LocalPoint[];
    /** Height of the tread, meters above the object's base. */
    top: number;
};

/** The outermost vertical face of a stage: the front polylines (3D-local meters) and how tall the face is. */
export type StageFace = { runs: LocalPoint[][]; top: number };

/**
 * The steps around a closed outline (a round stage) and the outermost front face that a riser style (grille, LED)
 * decorates. `heightM` is the height of the stage the steps lead up to. No steps for an open path, no steps
 * requested or no chosen edges; no face for an open path.
 */
export function stepModel(profile: ObjectProfile, heightM: number): { bands: StepBand[]; face: StageFace | null } {
    if (!profile.path.closed) return { bands: [], face: null };
    const steps = profile.steps;
    const count = steps ? Math.max(0, steps.count) : 0;
    const { points: raw, segmentOf: rawSegments } = profilePlanPoints(profile);
    if (raw.length < 3) return { bands: [], face: null };
    // Outward is the right of travel for a counter-clockwise ring.
    const flipped = ringArea(raw) < 0;
    const base = flipped ? [...raw].reverse() : raw;
    const n = raw.length;
    // The edge leaving base[i] is the raw piece starting at raw[i], or, reversed, the one starting at raw[n - 2 - i].
    const segmentFor = (i: number) => rawSegments[flipped ? (n - 2 - i + n) % n : i];
    const tread = steps ? steps.tread : 0;
    const rings: PlanPoint[][] = [base];
    for (let k = 1; k <= count; k += 1) {
        rings.push(offsetPath(base, true, k * tread, NO_BEVEL));
    }
    if (rings.some((ring) => ring.length !== base.length)) return { bands: [], face: null };

    // Which points the steps run along; the face is always the front.
    const outwards = offsetPath(base, true, 1, NO_BEVEL);
    const isFront = base.map((p, i) => {
        const nx = outwards[i].x - p.x;
        const ny = outwards[i].y - p.y;
        return ny / Math.hypot(nx, ny) <= -FRONT_NORMAL;
    });
    const edges = steps?.edges ?? 'front';
    const chosen = base.map((_, i) => {
        if (edges === 'all') return true;
        if (edges === 'front') return isFront[i];
        // The list names profile path segments; a point belongs to the segment that starts at it.
        return edges.includes(segmentFor(i));
    });

    const bands: StepBand[] = [];
    for (let k = 1; k <= count; k += 1) {
        const top = (heightM * (count + 1 - k)) / (count + 1);
        if (edges === 'all') {
            bands.push({ ring: toLocalRing(rings[k]), top });
            continue;
        }
        for (const run of edges === 'front' ? chosenRuns(chosen) : edgeRuns(chosen)) {
            const outer = run.map((i) => rings[k][i]);
            const inner = run.map((i) => rings[k - 1][i]).reverse();
            bands.push({ ring: toLocalRing([...outer, ...inner]), top });
        }
    }
    const faceRing = rings[count];
    const faceRuns = chosenRuns(isFront).map((run) => run.map((i) => planToLocal(faceRing[i])));
    const faceTop = count > 0 ? heightM / (count + 1) : heightM;
    return { bands, face: faceRuns.length > 0 ? { runs: faceRuns, top: faceTop } : null };
}

/** Runs of chosen path segments as the points they span: segment i runs from point i to the next point. */
function edgeRuns(chosen: readonly boolean[]): number[][] {
    const n = chosen.length;
    return chosenRuns(chosen, 1).map((run) => [...run, (run[run.length - 1] + 1) % n]);
}

/** Runs of consecutive chosen indices around a cycle (a run may wrap past the end). */
function chosenRuns(chosen: readonly boolean[], minLength = 2): number[][] {
    const n = chosen.length;
    if (chosen.every(Boolean)) return [Array.from({ length: n }, (_, i) => i)];
    if (!chosen.some(Boolean)) return [];
    const start = chosen.findIndex((c, i) => c && !chosen[(i + n - 1) % n]);
    const runs: number[][] = [];
    let current: number[] = [];
    for (let k = 0; k < n; k += 1) {
        const i = (start + k) % n;
        if (chosen[i]) current.push(i);
        else if (current.length > 0) {
            runs.push(current);
            current = [];
        }
    }
    if (current.length > 0) runs.push(current);
    return runs.filter((run) => run.length >= minLength);
}

/**
 * The path as polylines (plan mm) after removing the openings. A closed path is walked from its first point and
 * comes back to it. Without openings the whole path is one run.
 */
export function pathRuns(profile: ObjectProfile): PlanPoint[][] {
    const { points } = profilePlanPoints(profile);
    if (points.length < 2) return [];
    const line = profile.path.closed ? [...points, points[0]] : points;
    const openings = [...(profile.openings ?? [])].sort((a, b) => a.at - b.at);
    if (openings.length === 0) return [line];
    const cumulative: number[] = [0];
    for (let i = 1; i < line.length; i += 1) {
        cumulative.push(cumulative[i - 1] + Math.hypot(line[i].x - line[i - 1].x, line[i].y - line[i - 1].y));
    }
    const total = cumulative[cumulative.length - 1];
    // Kept intervals of arc length.
    let kept: [number, number][] = [[0, total]];
    for (const { at, width } of openings) {
        kept = kept.flatMap(([a, b]): [number, number][] => {
            if (at + width <= a || at >= b) return [[a, b]];
            const out: [number, number][] = [];
            if (at > a) out.push([a, at]);
            if (at + width < b) out.push([at + width, b]);
            return out;
        });
    }
    const pointAt = (s: number): PlanPoint => {
        let i = 1;
        while (i < cumulative.length - 1 && cumulative[i] < s) i += 1;
        const span = cumulative[i] - cumulative[i - 1] || 1;
        const t = (s - cumulative[i - 1]) / span;
        return {
            x: line[i - 1].x + (line[i].x - line[i - 1].x) * t,
            y: line[i - 1].y + (line[i].y - line[i - 1].y) * t
        };
    };
    return kept
        .filter(([a, b]) => b - a > 1)
        .map(([a, b]) => {
            const run: PlanPoint[] = [pointAt(a)];
            for (let i = 1; i < line.length - 1; i += 1) {
                if (cumulative[i] > a && cumulative[i] < b) run.push(line[i]);
            }
            run.push(pointAt(b));
            return run;
        });
}

/**
 * The outline of a wall of `thicknessMm` built along an open polyline (plan mm): the left offset then the reversed
 * right offset, a closed ring. Returns null for fewer than two points.
 */
export function wallRing(run: readonly PlanPoint[], thicknessMm: number): PlanPoint[] | null {
    if (run.length < 2) return null;
    const half = thicknessMm / 2;
    const right = offsetPath([...run], false, half, NO_BEVEL);
    const left = offsetPath([...run], false, -half, NO_BEVEL);
    return [...left, ...right.reverse()];
}

/** Points along a polyline every at most `spacing` mm, keeping the original points. */
export function resampleRun(run: readonly PlanPoint[], spacing: number): PlanPoint[] {
    const out: PlanPoint[] = [run[0]];
    for (let i = 1; i < run.length; i += 1) {
        const a = run[i - 1];
        const b = run[i];
        const parts = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / spacing));
        for (let k = 1; k <= parts; k += 1) {
            out.push({ x: a.x + ((b.x - a.x) * k) / parts, y: a.y + ((b.y - a.y) * k) / parts });
        }
    }
    return out;
}

/** Points at the arc-length positions `(i + 0.5) x length / count` of a polyline, `count = floor(length / spacing)`. */
export function pointsAlong(run: readonly PlanPoint[], spacing: number): PlanPoint[] {
    const lengths: number[] = [0];
    for (let i = 1; i < run.length; i += 1) {
        lengths.push(lengths[i - 1] + Math.hypot(run[i].x - run[i - 1].x, run[i].y - run[i - 1].y));
    }
    const total = lengths[lengths.length - 1];
    const count = Math.floor(total / spacing);
    const out: PlanPoint[] = [];
    let segment = 1;
    for (let k = 0; k < count; k += 1) {
        const at = ((k + 0.5) * total) / count;
        while (segment < lengths.length - 1 && lengths[segment] < at) segment += 1;
        const span = lengths[segment] - lengths[segment - 1] || 1;
        const t = (at - lengths[segment - 1]) / span;
        out.push({
            x: run[segment - 1].x + (run[segment].x - run[segment - 1].x) * t,
            y: run[segment - 1].y + (run[segment].y - run[segment - 1].y) * t
        });
    }
    return out;
}

/**
 * The path moved `distanceMm` toward the audience (plan -y, local +z): the sign of the offset follows the direction
 * the path was drawn, since "right of travel" is the audience side only for a path drawn left to right.
 */
export function towardAudience(run: readonly PlanPoint[], distanceMm: number): PlanPoint[] {
    const direction = run[run.length - 1].x - run[0].x;
    return offsetPath([...run], false, direction >= 0 ? distanceMm : -distanceMm, NO_BEVEL);
}
